import { Accordion } from "@heroui/react";
import { MinusIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import ReadyToJoin from "~/components/landingPage/ReadyToJoin";
import TopImageTitleAndShortDescription from "~/components/topImageTitleAndShortDescription";
import AccordionItemHtml from "~/components/ui/AccordionItemHtml";

export default function Resources() {
	const [activeKey, setActiveKey] = useState("0");

	const handleAccordionChange = (key: string) => {
		setActiveKey((prevKey) => (prevKey === key ? prevKey : key));
	};

	return (
		<div className="bg-white">
			<TopImageTitleAndShortDescription
				title="Resources"
				shortDescription="Find guidelines, templates, methodologies, and regulatory documents to support your project development and compliance."
				image="./resources.jpeg"
			/>
			<div className="bg-[#F4F4F5]">
				<div className="container mx-auto py-9 w-full sm:w-3/4 md:w-2/3 lg:w-1/2 text-center leading-7 description">
					<p>
						The Cambodia National Carbon Registry is committed to supporting
						project proponents, government agencies, and stakeholders in GHG
						emission reduction initiatives. This resource center provides access
						to essential guidelines, methodologies, and regulatory documents to
						ensure project compliance with national and international climate
						frameworks.
					</p>
				</div>
			</div>
			{/* <div className="container mx-auto space-y-12 p-5" id="how-it-words">
        <div>
          <p className="title text-center mt-4">Guidelines and Documents</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {guideLineData.map((item, index) => {
            return (
              <GuideLineCard
                key={index}
                title={item.title}
                itemList={item.itemList}
              />
            )
          })}
        </div>
      </div> */}
			<div className="container mx-auto max-w-5xl p-5">
				<div className="w-full bg-[#F4F4F5] relative">
					<div className="relative w-full min-h-[300px]">
						<img
							className="absolute inset-0 w-full h-full object-cover"
							src="/faq-image.webp"
							alt="FAQ Background"
						/>
						<div className="relative w-full p-4 sm:p-6 md:p-8 lg:p-12 bg-[#F5F5F580]">
							<div>
								<p className="text-center title">
									Frequently Asked Questions (FAQs)
								</p>
							</div>
							<div className="px-0 sm:px-4 md:px-6 lg:px-24 py-4 sm:py-6 md:py-8 lg:py-10">
								<Accordion
									dividerProps={{ hidden: true }}
									selectedKeys={[activeKey]}
								>
									<AccordionItemHtml
										key={"faq-1"}
										title="Want to know about the Cambodia Carbon Registry (CCR) System?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-1")}
									>
										<div className="space-y-4">
											<p>
												The Cambodia Carbon Registry (CCR): A Cornerstone of
												Cambodia&rsquo;s Climate Action
											</p>
											<p>
												The Cambodia Carbon Registry (CCR) is a national digital
												platform designed to track, manage, and report on carbon
												credits throughout their lifecycle. It monitors the
												creation, validation, ownership, transfer, cancellation,
												and retirement of these credits. As a critical component
												of Cambodia&rsquo;s climate change mitigation strategy,
												the CCR aligns with the Pentagonal Strategy, the
												Long-Term Strategy on Carbon Neutrality, and the
												Nationally Determined Contributions (NDC3.0).
											</p>
											<p>Development Partnership</p>
											<p>
												The Ministry of Environment (MoE) of the Royal
												Government of Cambodia, in collaboration with the United
												Nations Development Programme (UNDP), has developed this
												open-source National Carbon Registry system. Tailored to
												meet Cambodia&rsquo;s specific needs, it ensures
												alignment with both national priorities and
												international standards.
											</p>
											<p>Key functions of the Cambodia Carbon Registry (CCR)</p>
											<p>
												The CCR is built to ensure transparency and integrity in
												carbon markets, adhering to Article 6 Operational Manual
												and other global benchmarks:&nbsp;
											</p>
											<ol className="space-y-4 list-decimal pl-6">
												<li>
													Credit lifecycle tracking: The registry records every
													stage of a carbon credit&rsquo;s journey&mdash;from
													issuance by certified projects to transfer between
													buyers and sellers, and final retirement.
												</li>
												<li>
													Preventing double counting: Each verified credit is
													assigned a unique serial number, ensuring no single
													emission reduction is claimed by multiple entities.
												</li>
												<li>
													Interoperability: The CCR is designed to integrate
													with international systems, such as the UN&rsquo;s
													registry under Article 6 of the Paris Agreement. This
													facilitates transparent cross-border transfers of
													carbon credits, including Internationally Transferred
													Mitigation Outcomes (ITMOs) and the Paris Agreement
													Crediting Mechanism (PACM).
												</li>
												<li>
													Compliance management: The platform helps the
													government monitor progress toward its Nationally
													Determined Contributions (NDCs) by providing real-time
													insights into carbon credit flows and project
													performance.
												</li>
												<li>
													Enhanced transparency: A public ledger of transactions
													ensures transparency, preventing
													&ldquo;greenwashing&rdquo; and fostering trust in
													carbon offset claims.
												</li>
												<li>
													Benefit sharing: Over time, the CCR aims to track
													revenue distribution from carbon projects, ensuring
													equitable sharing of benefits with local communities,
													particularly for land-based initiatives.&nbsp;
												</li>
											</ol>
											<p>Benefits for Cambodia and Carbon Markets</p>
											<p>
												The CCR has been designed to ensure a robust national
												carbon registry offers significant advantages for
												Cambodia&rsquo;s climate strategy and economic
												development:&nbsp;
											</p>
											<ol className="space-y-4 list-decimal pl-6">
												<li>
													Attracts investment: By guaranteeing a credible and
													transparent system, the CCR builds investor confidence
													in Cambodia&rsquo;s carbon market, unlocking
													international climate finance opportunities.
												</li>
												<li>
													Customization: The open-source nature of the platform
													allows Cambodia to adapt it to its unique regulatory,
													economic, and environmental priorities.
												</li>
												<li>
													Reduced costs: Leveraging UNDP-led technical
													assistance and open-source software minimizes the
													financial and time investments required to build
													complex digital infrastructure from scratch.
												</li>
												<li>
													Economic asset: By treating carbon credits as a
													national asset with clear ownership tracking, Cambodia
													can leverage these resources to accelerate green
													economic growth and achieve its Sustainable
													Development Goals (SDGs).
												</li>
											</ol>
											<p>
												While the CCR offers immense benefits, its
												implementation and operation present several challenges:
											</p>
											<ol>
												<li>
													Interoperability: Fragmented global standards require
													robust design to ensure seamless integration with
													international systems, avoiding inefficiencies and
													credibility issues.
												</li>
												<li>
													Capacity and governance: Effective implementation
													demands not only advanced technology but also strong
													institutional capacity, clear policies, and governance
													frameworks to oversee carbon markets effectively.
												</li>
												<li>
													Credit quality: The registry&rsquo;s credibility
													hinges on the quality of its credits. Projects must
													undergo rigorous verification to ensure genuine
													emission reductions and avoid overstated claims.
												</li>
											</ol>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-2"}
										title="Who can submit a project for registration?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-2")}
									>
										<div className="space-y-4">
											<p>Project submissions are open but not limited to:</p>
											<ul className="space-y-4 list-decimal pl-6">
												<li>Government agencies</li>
												<li>Private sector entities</li>
												<li>Owners of the project or land</li>
												<li>Non-governmental organizations (NGOs)</li>
												<li>Community-based organizations (CBOs)</li>
												<li>International development partners</li>
											</ul>
											<p>
												All applicants must meet the eligibility criteria and
												comply with submission guidelines.
											</p>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-3"}
										title="What are the eligibility requirements for project submission?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-3")}
									>
										<div className="space-y-4">
											<p>To be eligible for registration, projects must:</p>
											<ul className="space-y-4 list-decimal pl-6">
												<li>
													<strong>
														Fall under one of the approved sectors
													</strong>
													: Forestry and Other Land Use (FOLU), Agriculture,
													Energy, Industrial Processes and Product Use (IPPU),
													or Waste Management.
												</li>
												<li>
													<strong>
														Demonstrate measurable emission reductions
													</strong>{" "}
													using recognized methodologies.
												</li>
												<li>
													<strong>
														Comply with Cambodia&rsquo;s climate policies
													</strong>{" "}
													and international mechanisms (e.g., REDD+, Paris
													Agreement).
												</li>
												<li>
													<strong>
														Have a clear monitoring and reporting framework
													</strong>{" "}
													for verification.
												</li>
											</ul>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-4"}
										title=" What documents are required for project submission?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-4")}
									>
										<div className="space-y-4">
											<p>Applicants must submit:</p>
											<ul className="space-y-4 list-decimal pl-6">
												<li>
													<strong>Project Design Document (PDD)</strong> &ndash;
													Detailed description of project activities and
													expected emission reductions.
												</li>
												<li>
													<strong>Baseline &amp; Additionality Report</strong>{" "}
													&ndash; Demonstrates the project's impact.
												</li>
												<li>
													<strong>Monitoring &amp; Reporting Plan</strong>{" "}
													&ndash; Specifies how emission reductions will be
													tracked.
												</li>
												<li>
													<strong>
														Environmental &amp; Social Safeguards Plan
													</strong>{" "}
													&ndash; Addresses risks and mitigation strategies.
												</li>
												<li>
													<strong>
														Legal Ownership &amp; Land Use Documentation
													</strong>{" "}
													&ndash; Confirms project rights and compliance.
												</li>
												<li>
													Refer to the{" "}
													<strong>Project Submission Guidelines</strong> for a
													complete list.
												</li>
											</ul>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-5"}
										title="How long does the project review and approval process take?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-5")}
									>
										<div className="space-y-4">
											<p>
												The review process typically involves the following
												steps:
											</p>
											<ul className="space-y-4 list-decimal pl-6">
												<li>
													<strong>Initial Concept Review</strong>: 2&ndash;4
													weeks
												</li>
												<li>
													<strong>
														Full Project Submission &amp; Technical Review
													</strong>
													: 6&ndash;12 weeks
												</li>
												<li>
													<strong>Government Assessment &amp; Approval</strong>:
													8&ndash;16 weeks
												</li>
												<li>
													<strong>
														Final Registration &amp; Issuance of Unique Project
														ID
													</strong>
													: 4 weeks
												</li>
											</ul>
											<p>
												Timelines may vary depending on project complexity and
												compliance with requirements.
											</p>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-6"}
										title="How is project data monitored and verified?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-6")}
									>
										<div className="space-y-4">
											<p>
												All registered projects must follow a robust{" "}
												<strong>
													Monitoring, Reporting, and Verification (MRV) process
												</strong>
												, which includes:
											</p>
											<ul className="space-y-4 list-decimal pl-6">
												<li>
													<strong>Regular data collection and reporting</strong>{" "}
													(annual or periodic submissions).
												</li>
												<li>
													<strong>Independent third-party verification</strong>{" "}
													by accredited auditors.
												</li>
												<li>
													<strong>Expert review and compliance checks</strong>{" "}
													to ensure continued eligibility and compliance.
												</li>
											</ul>
											<p>
												Failure to meet MRV requirements may result in project
												suspension or deregistration.
											</p>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-7"}
										title="Can a project be modified after registration?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-7")}
									>
										<div className="space-y-4">
											<p>
												Yes, project proponents can{" "}
												<strong>request modifications</strong> if there are
												significant changes in:
											</p>
											<ul className="space-y-4 list-decimal pl-6">
												<li>
													<strong>Project activities or methodologies</strong>
												</li>
												<li>
													<strong>Emission reduction calculations</strong>
												</li>
												<li>
													<strong>Stakeholder engagement plans</strong>
												</li>
												<li>
													<strong>Monitoring and reporting protocols</strong>
												</li>
											</ul>
											<p>
												Any modifications must be justified by the project
												proponent and be reviewed and approved by the regulatory
												authorities before implementation.
											</p>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-8"}
										title="What happens if a project does not comply with the guidelines?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-8")}
									>
										<div className="space-y-4">
											<p>Non-compliant projects may face:</p>
											<ul className="space-y-4 list-decimal pl-6">
												<li>
													<strong>Requests for corrective actions</strong>{" "}
													within a given timeframe.
												</li>
												<li>
													<strong>Temporary suspension</strong> until compliance
													is restored.
												</li>
												<li>
													<strong>Permanent deregistration</strong> for repeated
													or severe violations.
												</li>
											</ul>
											<p>
												To avoid compliance issues, project developers should
												follow the guidelines carefully and seek support when
												needed.
											</p>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-9"}
										title="Is there any financial or technical support available for project developers?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-9")}
									>
										<div className="space-y-4">
											<p>
												The Cambodia National Carbon Registry System does not
												provide financial support but may facilitate access to:
											</p>
											<ul className="space-y-4 list-decimal pl-6">
												<li>
													<strong>Government grants and incentives</strong> for
													climate projects.
												</li>
												<li>
													<strong>International funding opportunities</strong>{" "}
													through climate finance mechanisms.
												</li>
												<li>
													<strong>Technical support and training</strong> for
													project developers, including workshops on MRV
													compliance.
												</li>
											</ul>
											<p>
												For more information, check the{" "}
												<a href="/resources">
													<strong>Resources Page</strong>
												</a>{" "}
												or contact our support team.
											</p>
										</div>
									</AccordionItemHtml>
									<AccordionItemHtml
										key={"faq-10"}
										title="How can I get additional support or training?"
										classNames={{
											indicator: "data-[open=true]:-rotate-0",
											title: "text-xl data-[open=true]:font-bold",
											content: "py-0 pb-4",
										}}
										indicator={({ isOpen }) =>
											isOpen ? <MinusIcon /> : <PlusIcon />
										}
										onPress={() => handleAccordionChange("faq-10")}
									>
										<div className="space-y-4">
											<p>For assistance, you can:</p>
											<p>
												<strong>Email us at:</strong>{" "}
												support@carbonregistry.gov.kh
											</p>
											<p>
												<strong>Call our helpline:</strong>&nbsp;+855 23 213 908
											</p>
											<p>
												<strong>Visit our office:</strong> Ministry of
												Environment, Cambodia
											</p>
											<p>
												<strong>Join our training programs:</strong> Check our
												website for upcoming workshops and webinars.
											</p>
										</div>
									</AccordionItemHtml>
								</Accordion>
							</div>
						</div>
					</div>
				</div>
			</div>
			<ReadyToJoin />
		</div>
	);
}
